import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { Crown, Gem, Medal, Trophy, User, ArrowRight } from "lucide-react";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(840px at 12% 2%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(920px at 88% 8%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Wrap = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const Hero = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.18);
  color: #fcd34d;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: 42px;
  line-height: 1.05;
  font-weight: 1000;
`;

const Sub = styled.p`
  margin: 14px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
`;

const List = styled.div`
  margin-top: 24px;
  display: grid;
  gap: 14px;
`;

const MilestonePanel = styled.div`
  margin-top: 24px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const MilestoneGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const MilestoneCard = styled.div`
  padding: 16px;
  border-radius: 18px;
  background: ${({ $tone }) =>
    $tone === "platinum"
      ? "linear-gradient(135deg, rgba(56, 189, 248, 0.18), rgba(186, 230, 253, 0.08))"
      : $tone === "gold"
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(250, 204, 21, 0.08))"
      : $tone === "silver"
        ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(226, 232, 240, 0.06))"
        : "linear-gradient(135deg, rgba(249, 115, 22, 0.14), rgba(251, 146, 60, 0.06))"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "platinum"
        ? "rgba(56, 189, 248, 0.32)"
        : $tone === "gold"
        ? "rgba(245, 158, 11, 0.24)"
        : $tone === "silver"
          ? "rgba(148, 163, 184, 0.22)"
          : "rgba(249, 115, 22, 0.22)"};
`;

const MilestoneTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const MilestoneValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  font-weight: 1000;
`;

const MilestoneHint = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto auto;
  gap: 16px;
  align-items: center;
  padding: 18px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);

  @media (max-width: 760px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`;

const Rank = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f8fafc;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.22), rgba(56, 189, 248, 0.18));
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const Meta = styled.div`
  min-width: 0;
`;

const Name = styled.div`
  color: #f8fafc;
  font-size: 16px;
  font-weight: 900;
`;

const Details = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
`;

const Value = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
  text-align: right;

  @media (max-width: 760px) {
    text-align: left;
  }
`;

const MilestoneText = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "platinum"
      ? "rgba(125, 211, 252, 0.16)"
      : $tone === "gold"
      ? "rgba(245, 158, 11, 0.14)"
      : $tone === "silver"
        ? "rgba(148, 163, 184, 0.14)"
        : "rgba(249, 115, 22, 0.14)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "platinum"
        ? "rgba(125, 211, 252, 0.28)"
        : $tone === "gold"
        ? "rgba(245, 158, 11, 0.28)"
        : $tone === "silver"
          ? "rgba(148, 163, 184, 0.28)"
          : "rgba(249, 115, 22, 0.24)"};
  color: ${({ $tone }) =>
    $tone === "platinum"
      ? "#bae6fd"
      : $tone === "gold"
        ? "#fde68a"
        : $tone === "silver"
          ? "#e2e8f0"
          : "#fdba74"};
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ProfileBtn = styled.button`
  height: 42px;
  padding: 0 16px;
  border-radius: 14px;
  border: 1px solid rgba(56, 189, 248, 0.22);
  background: rgba(56, 189, 248, 0.1);
  color: #e0f2fe;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Empty = styled.div`
  margin-top: 24px;
  padding: 24px;
  border-radius: 24px;
  text-align: center;
  color: #94a3b8;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SkeletonBlock = styled.div`
  border-radius: ${({ $rounded = 16 }) => `${$rounded}px`};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 255, 255, 0.12) 37%,
    rgba(255, 255, 255, 0.05) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

function getLevel(coins) {
  if (coins >= 10000) return { name: "Platinum", tone: "platinum", icon: Gem };
  if (coins >= 5000) return { name: "Gold", tone: "gold", icon: Crown };
  if (coins >= 1000) return { name: "Silver", tone: "silver", icon: Medal };
  return { name: "Bronze", tone: "bronze", icon: Trophy };
}

function getMilestoneMeta(coins) {
  if (coins >= 10000) {
    return {
      text: "Platinum milestone unlocked",
    };
  }

  if (coins >= 5000) {
    return {
      text: `${10000 - coins} coins to reach Platinum`,
    };
  }

  if (coins >= 1000) {
    return {
      text: `${5000 - coins} coins to reach Gold`,
    };
  }

  return {
    text: `${1000 - coins} coins to reach Silver`,
  };
}

export default function LearnersPage() {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const userSnap = await getDocs(
          query(collection(db, "users"), orderBy("coins", "desc"), limit(25))
        );

        const rows = await Promise.all(
          userSnap.docs.map(async (snap, index) => {
            const userData = snap.data() || {};
            const roadmapSnap = await getDoc(doc(db, "roadmaps", snap.id));
            const roadmapData = roadmapSnap.exists() ? roadmapSnap.data() : {};
            return {
              id: snap.id,
              rank: index + 1,
              name: userData.name || "Learner",
              coins: Number(userData.coins || 0),
              level: getLevel(Number(userData.coins || 0)),
              targetRole:
                roadmapData?.direction?.role ||
                roadmapData?.direction?.title ||
                "No roadmap yet",
              completedDays: Object.keys(roadmapData?.completedDays || {}).length,
            };
          })
        );

        if (active) setLearners(rows);
      } catch (error) {
        if (active) setLearners([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Page>
      <Wrap>
        <Hero>
          <Eyebrow>
            <Trophy size={14} />
            Learner Momentum
          </Eyebrow>
          <Title>See who is building consistent momentum</Title>
          <Sub>
            A public list of learners showing up, earning coins, and turning roadmap execution into visible proof.
          </Sub>
        </Hero>

        <MilestonePanel>
          <Sub style={{ margin: 0, maxWidth: "100%" }}>
            Complete one roadmap day to earn <strong style={{ color: "#f8fafc" }}>100 coins</strong>. Levels unlock like this:
          </Sub>
          <MilestoneGrid style={{ marginTop: 16 }}>
            <MilestoneCard $tone="bronze">
              <MilestoneTitle>
                <Trophy size={14} />
                Bronze Learner
              </MilestoneTitle>
              <MilestoneValue>0+ coins</MilestoneValue>
              <MilestoneHint>Starting level for every learner building daily momentum.</MilestoneHint>
            </MilestoneCard>

            <MilestoneCard $tone="silver">
              <MilestoneTitle>
                <Medal size={14} />
                Silver Learner
              </MilestoneTitle>
              <MilestoneValue>1000+ coins</MilestoneValue>
              <MilestoneHint>Shows stronger consistency and visible roadmap follow-through.</MilestoneHint>
            </MilestoneCard>

            <MilestoneCard $tone="gold">
              <MilestoneTitle>
                <Crown size={14} />
                Gold Learner
              </MilestoneTitle>
              <MilestoneValue>5000+ coins</MilestoneValue>
              <MilestoneHint>Reserved for learners building long-term execution proof inside the product.</MilestoneHint>
            </MilestoneCard>

            <MilestoneCard $tone="platinum">
              <MilestoneTitle>
                <Gem size={14} />
                Platinum Learner
              </MilestoneTitle>
              <MilestoneValue>10000+ coins</MilestoneValue>
              <MilestoneHint>For learners showing sustained execution and long-term proof of work.</MilestoneHint>
            </MilestoneCard>
          </MilestoneGrid>
        </MilestonePanel>

        {loading ? (
          <List>
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index}>
                <SkeletonBlock style={{ width: 42, height: 42, borderRadius: 14 }} />
                <SkeletonBlock style={{ width: 44, height: 44, borderRadius: 16 }} />
                <div>
                  <SkeletonBlock style={{ width: 180, height: 18 }} />
                  <SkeletonBlock style={{ width: 260, height: 14, marginTop: 10 }} />
                </div>
                <SkeletonBlock style={{ width: 90, height: 16 }} />
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <SkeletonBlock style={{ width: 94, height: 34, borderRadius: 999 }} />
                  <SkeletonBlock style={{ width: 124, height: 42, borderRadius: 14 }} />
                </div>
              </Card>
            ))}
          </List>
        ) : learners.length === 0 ? (
          <Empty>No learner activity found yet.</Empty>
        ) : (
          <List>
            {learners.map((learner) => {
              const LevelIcon = learner.level.icon;
              const milestone = getMilestoneMeta(learner.coins);
              return (
                <Card key={learner.id}>
                  <Rank>{learner.rank}</Rank>
                  <Avatar>
                    <User size={18} />
                  </Avatar>
                  <Meta>
                    <Name>{learner.name}</Name>
                    <Details>
                      {learner.targetRole} • {learner.completedDays} days completed
                    </Details>
                  </Meta>
                  <Value>
                    {learner.coins} coins
                    <MilestoneText>{milestone.text}</MilestoneText>
                  </Value>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <Pill $tone={learner.level.tone}>
                      <LevelIcon size={13} />
                      {learner.level.name}
                    </Pill>
                    <ProfileBtn onClick={() => navigate(`/profile/${learner.id}`)}>
                      View Profile
                      <ArrowRight size={14} />
                    </ProfileBtn>
                  </div>
                </Card>
              );
            })}
          </List>
        )}
      </Wrap>
    </Page>
  );
}
